import { getDocumentReferences } from '@ctw/shared/api/fhir/document';
import { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { fqsRequestAll, MAX_OBJECTS_PER_REQUEST } from '@ctw/shared/api/fqs/client';
import {
  type EncounterGraphqlResponse,
  encountersQuery,
} from '@ctw/shared/api/fqs/queries/encounters';
import { dedupeAndMergeEncounters } from '@ctw/shared/content/encounters/helpers/filters';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { GraphQLClient } from 'graphql-request';
import { SYSTEM_SUMMARY } from './system-urls';

export async function getEncountersWithClinicalNotes(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
) {
  const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
    telemetry,
    graphqlClient,
    encountersQuery,
    'EncounterConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {},
    },
  );

  const documents = await getDocumentReferences(telemetry, graphqlClient, patient, [
    MAX_OBJECTS_PER_REQUEST,
  ]);

  const results = data.EncounterConnection.edges
    .map((x) => x.node)
    .map((c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList));

  results.forEach((e) => {
    e.setClinicalNotesFromDocumentPool(documents);
  });

  return dedupeAndMergeEncounters(results);
}

export async function getEncountersById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string>,
  includeClinicalNotes = true,
) {
  const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
    telemetry,
    graphqlClient,
    encountersQuery,
    'EncounterConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {},
      filter: {
        ids: {
          anymatch: ids,
        },
      },
    },
  );

  const results = data.EncounterConnection.edges
    .map((x) => x.node)
    .map((c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList));

  if (includeClinicalNotes) {
    const documents = await getDocumentReferences(telemetry, graphqlClient, patient, [
      MAX_OBJECTS_PER_REQUEST,
    ]);
    results.forEach((e) => {
      e.setClinicalNotesFromDocumentPool(documents);
    });
  }

  return dedupeAndMergeEncounters(results);
}

// TODO: Remove this function once the Encounter lens is tested.
export function getNonLensEncountersFQS(limit: number, ids?: Array<string>) {
  return async (
    telemetry: ReturnType<typeof useTelemetry>,
    graphqlClient: GraphQLClient,
    patient: PatientModel,
  ) => {
    // Skip querying for an empty set of ids.
    // This fixes an issue with FQS where it is an error to do an anymatch with an empty array.
    if (ids !== undefined && ids.length === 0) {
      return [];
    }
    const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
      telemetry,
      graphqlClient,
      encountersQuery,
      'EncounterConnection',
      {
        upid: patient.UPID,
        cursor: '',
        first: limit,
        // Optionally filter down to a specific set of encounters.
        filter: {
          ...(ids
            ? {
                ids: { anymatch: ids },
              }
            : {}),
          tag: { nonematch: [SYSTEM_SUMMARY] },
        },
      },
    );
    const results = data.EncounterConnection.edges
      .map((x) => x.node)
      .map((c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList));
    return dedupeAndMergeEncounters(results);
  };
}
